/**
 * 我的聯絡人 API Service
 * 注意：axios 攔截器自動從 localStorage 讀取 txn_token 並添加到所有請求的 header
 * Service 層不需要手動傳遞 txn_token 參數
 */

import axiosInstance from '../api/axios';
import { EeMyContact, EeMyContactCreate, EeMyContactUpdate } from '../types/eeMyContacts';

const BASE = '/api/ee_my_contacts';

export interface ContactListResult {
  total: number;              // filter 後全筆數（供分頁 + 「所有聯絡人」總數）
  items: EeMyContact[];       // 當頁
}

export const getContacts = async (params?: {
  search?: string;
  category?: string;
  group_id?: number;          // server-side 群組 filter（#19，取代前端 client filter）
  no_group?: boolean;         // 只回未分組成員（#20；與 group_id 互斥）
  skip?: number;
  limit?: number;
}): Promise<ContactListResult> => {
  const res = await axiosInstance.get<ContactListResult>(`${BASE}/`, { params });
  return res.data;
};

export const getContact = async (id: number): Promise<EeMyContact> => {
  const res = await axiosInstance.get<EeMyContact>(`${BASE}/${id}`);
  return res.data;
};

export const createContact = async (data: EeMyContactCreate): Promise<EeMyContact> => {
  const res = await axiosInstance.post<EeMyContact>(`${BASE}/`, data);
  return res.data;
};

export const updateContact = async (id: number, data: EeMyContactUpdate): Promise<EeMyContact> => {
  const res = await axiosInstance.put<EeMyContact>(`${BASE}/${id}`, data);
  return res.data;
};

export const deleteContact = async (id: number): Promise<void> => {
  await axiosInstance.delete(`${BASE}/${id}`);
};

// ── Google OAuth ──

export interface GoogleStatus {
  connected: boolean;
  scope?: string;
  expires_at?: string | null;
  expired?: boolean;
}

export interface GoogleSyncStats {
  created: number;
  updated: number;
  skipped: number;
  groups_synced: number;
}

export const getGoogleAuthUrl = async (returnTo: string): Promise<{ auth_url: string }> => {
  const res = await axiosInstance.get(`${BASE}/google/auth-url`, { params: { return_to: returnTo } });
  return res.data;
};

export const getGoogleStatus = async (): Promise<GoogleStatus> => {
  const res = await axiosInstance.get<GoogleStatus>(`${BASE}/google/status`);
  return res.data;
};

export const syncFromGoogle = async (groupId?: number | null): Promise<{ ok: boolean; stats: GoogleSyncStats }> => {
  const params = groupId ? { group_id: groupId } : undefined;
  const res = await axiosInstance.post(`${BASE}/google/sync`, null, { params });
  return res.data;
};

export const disconnectGoogle = async (): Promise<void> => {
  await axiosInstance.delete(`${BASE}/google/disconnect`);
};

// ── vCard Import ──

export interface VcardImportStats {
  created: number;
  skipped: number;
  errors: string[];
}

export const importVcard = async (file: File, groupId?: number | null): Promise<VcardImportStats> => {
  const formData = new FormData();
  formData.append('file', file);
  const params = groupId ? { group_id: groupId } : undefined;
  const res = await axiosInstance.post<VcardImportStats>(`${BASE}/import-vcard`, formData, {
    params,
    headers: { 'Content-Type': 'multipart/form-data' },   // axiosInstance 預設 json；FormData 必覆蓋，否則被 formDataToJSON 轉空 body（[[axios-formdata-multipart-override]]）
  });
  return res.data;
};

// ── Business Card Scan ──

export interface CardScanParsed {
  display_name: string;
  org?: string;
  title?: string;
  phones: { type: string; value: string }[];
  emails: { type: string; value: string }[];
}

export interface CardScanResult {
  raw_text: string;
  parsed: CardScanParsed;
}

export const scanCard = async (file: File): Promise<CardScanResult> => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await axiosInstance.post<CardScanResult>(`${BASE}/scan-card`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },   // 同 importVcard：FormData 必覆蓋預設 json
  });
  return res.data;
};

export const uploadCardImage = async (file: File): Promise<{ url: string; file_object_id?: number | null }> => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await axiosInstance.post<{ url: string; file_object_id?: number | null }>(`${BASE}/upload-card-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },   // 同 importVcard：FormData 必覆蓋預設 json
  });
  return res.data;
};

/** 名片照 reader endpoint（G3.2：走加密 file_object，需 AuthImage 帶 token）。 */
export const cardImageUrl = (contactId: number, index: number): string =>
  `${BASE}/${contactId}/card-image/${index}`;
